import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import axios from "axios";
import { toast } from "react-toastify";
import { StyledButton } from "../../doctor-console/StyledComponents";
import { isTodayOrFuture } from "../../utils/utils";
import PhysioEditableSection from "./PhysioEditableSection";
import PrintPhysiotherapy from "./PrintPhysiotherapy";

const clinicalStatusOptions = ["Active", "Inactive", "Resolved", "Remission", "Recurrence"];

export default function PhysiotherapySheetModal({
  open,
  onClose,
  appointment,
  patientData,
  selectedDoctorData,
  dateForAppointment,
  isAbdmEnabled,
  onCompleteAppointment,
}) {
  const [careContexts, setCareContexts] = useState([]);
  const [selectedContext, setSelectedContext] = useState("");
  const [selectedContextDisplay, setSelectedContextDisplay] = useState("");
  const [newVisitPurpose, setNewVisitPurpose] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [printAnchorEl, setPrintAnchorEl] = useState(null);

  const [chiefComplaint, setChiefComplaint] = useState([]);
  const [knownCaseOf, setKnownCaseOf] = useState([]);
  const [historyOf, setHistoryOf] = useState([]);
  const [onObservation, setOnObservation] = useState([]);
  const [upperLimbExam, setUpperLimbExam] = useState([]);
  const [lowerLimbExam, setLowerLimbExam] = useState([]);
  const [advice, setAdvice] = useState([]);
  const previousAutoObservationDateRef = useRef("");

  const printRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const authToken = sessionStorage.getItem("accessToken") || "";
  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

  const doctorName = selectedDoctorData?.name || "Doctor";
  const doctorId = selectedDoctorData?.doctor_id || "";
  const patientId = patientData?.patient_id || appointment?.patient || "";
  const facilityId = sessionStorage.getItem("facility_name") || "";
  // const facilityId = sessionStorage.getItem("facility_id") || "";
  const orgCode = sessionStorage.getItem("ORG_CODE") || "";
  const appMode = sessionStorage.getItem("APP_MODE") || "SaaS";

  const resetFormState = () => {
    setSelectedContext("");
    setSelectedContextDisplay("");
    setNewVisitPurpose("");
    setSubmitted(false);
    setPrintAnchorEl(null);
    setChiefComplaint([]);
    setKnownCaseOf([]);
    setHistoryOf([]);
    setOnObservation([]);
    setUpperLimbExam([]);
    setLowerLimbExam([]);
    setAdvice([]);
    setCareContexts([]);
    previousAutoObservationDateRef.current = "";
  };

  const handleModalClose = () => {
    resetFormState();
    onClose?.();
  };

  const getCurrentDateTime = () => {
    if (isTodayOrFuture(dateForAppointment)) return new Date().toISOString();
    return new Date(dateForAppointment || new Date()).toISOString();
  };

  const getSelectedCareContextDate = () => {
    const selected = careContexts.find(
      (item) => item.care_context_reference === selectedContext
    );
    const createdAt =
      selected?.care_context_created_at ||
      selected?.created_at ||
      selected?.care_context_created_on ||
      selected?.date ||
      selected?.timestamp ||
      null;
    if (!createdAt) return getCurrentDateTime().split("T")[0];
    const parsed = new Date(createdAt);
    if (Number.isNaN(parsed.getTime())) return getCurrentDateTime().split("T")[0];
    return parsed.toISOString().split("T")[0];
  };

  React.useEffect(() => {
    const nextAutoDate = getSelectedCareContextDate();
    const previousAutoDate = previousAutoObservationDateRef.current;

    // First pass: initialize baseline auto date reference.
    if (!previousAutoDate) {
      previousAutoObservationDateRef.current = nextAutoDate;
      return;
    }

    // On care context change, update only untouched/auto-filled dates.
    const updateObservationDate = (rows) =>
      (rows || []).map((item) => {
        const currentDate = item?.observation_date || "";
        const isManual = Boolean(item?.observation_date_manual);
        if (!isManual && (!currentDate || currentDate === previousAutoDate)) {
          return {
            ...item,
            observation_date: nextAutoDate,
            observation_date_manual: false,
          };
        }
        return item; // Keep user-edited date.
      });

    setOnObservation((prev) => updateObservationDate(prev));
    setUpperLimbExam((prev) => updateObservationDate(prev));
    setLowerLimbExam((prev) => updateObservationDate(prev));

    previousAutoObservationDateRef.current = nextAutoDate;
  }, [selectedContext]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCareContexts = async () => {
    if (!patientId) return;
    try {
      const postData = {
        filter_type: isAbdmEnabled === "true" ? "patient_abha" : "patient_id",
        filter_details: {
          ...(isAbdmEnabled === "true"
            ? { patient_abha: patientData?.abha_id || "" }
            : { patient_id: patientId }),
        },
      };
      const response = await axios.post(
        `${backendUrl}fetch_care_contexts_by_filter/`,
        JSON.stringify(postData),
        { headers }
      );
      const list = response?.data?.care_contenxts || [];
      setCareContexts(list);
      if (list.length > 0) {
        setSelectedContext(list[0].care_context_reference);
        setSelectedContextDisplay(list[0].display);
      }
    } catch (error) {
      toast.error("Failed to fetch visit purpose list.");
    }
  };

  React.useEffect(() => {
    if (!open) return;
    // Always start with a fresh form when modal opens for a patient.
    resetFormState();
    fetchCareContexts();
  }, [open, patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchCdac = async (term, semanticTag = "finding++disorder") => {
    try {
      const postBody = {
        term,
        state: "active",
        semantictag: semanticTag,
        acceptability: "synonyms",
        returnlimit: "100",
        refsetid: null,
        parentid: null,
        excludeparentconcept: false,
        fullconcept: false,
      };
      const response = await axios.post(`${backendUrl}snomed/search/`, postBody, { headers });
      const list = response?.data?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        return list.slice(0, 100).map((item) => ({ term: item.term, id: item.conceptId }));
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const addCareContext = async () => {
    if (!newVisitPurpose.trim()) {
      toast.error("Visit details are required.");
      return;
    }
    const careContextCreatedAt = isTodayOrFuture(dateForAppointment)
      ? null
      : (() => {
        const startTimeRaw = appointment?.start_time || appointment?.start_time_ist || "";
        const hhmm = String(startTimeRaw).slice(11, 16);
        const [hours, minutes] = (hhmm || "00:00").split(":").map(Number);
        const date = new Date(
          dateForAppointment.getFullYear(),
          dateForAppointment.getMonth(),
          dateForAppointment.getDate(),
          Number.isFinite(hours) ? hours : 0,
          Number.isFinite(minutes) ? minutes : 0
        );
        const pad = (n) => String(n).padStart(2, "0");
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
          date.getUTCDate()
        )} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
      })();

    try {
      await axios.post(
        `${backendUrl}create_care_context/`,
        JSON.stringify({
          patient_id: patientId,
          visit_details: newVisitPurpose.trim(),
          care_context_created_at: careContextCreatedAt,
        }),
        { headers }
      );
      setNewVisitPurpose("");
      await fetchCareContexts();
      toast.success("Visit purpose added.");
    } catch (error) {
      toast.error("Unable to add visit purpose.");
    }
  };

  const toConditionPayload = (resourceType, rows) =>
    rows.map((item) => ({
      resource_type: resourceType,
      disease_description: item.disease_description || null,
      disease_code: item.disease_code || null,
      clinical_status_description: item.clinical_status_description || null,
      recorded_date: item.recorded_date || getCurrentDateTime(),
      ...(resourceType === "HistoryOf"
        ? {
          onset_datetime: item.onset_datetime || null,
        }
        : {}),
    }));

  const toObservationPayload = (resourceType, rows) =>
    rows.map((item) => ({
      resource_type: resourceType,
      observation_description: item.observation_description || null,
      observation_code: item.observation_code || null,
      ...((resourceType === "UpperLimbExam")
        ? {
          value_quantity:
            item.value_quantity !== "" && item.value_quantity !== null
              ? Number(item.value_quantity)
              : null,
        }
        : {}),
      value_string: item.value_string || null,
      ...(resourceType !== "PhysioObservation"
        ? {
          body_site_code: item.body_site_code || null,
          body_site_description: item.body_site_description || null,
        }
        : {}),
      ...(resourceType === "UpperLimbExam"
        ? {
          unit: item.unit || null,
        }
        : {}),
      observation_date: item.observation_date || getSelectedCareContextDate(),
    }));

  const buildAdviceAsOtherObservation = (rows) => {
    const adviceText = (rows?.[0]?.advice_text || "").trim();
    if (!adviceText) return [];
    return [
      {
        resource_type: "OtherObservation",
        observation_description: "Medical Advice",
        observation_code: null,
        value_quantity: null,
        value_string: adviceText,
        value_boolean: null,
        value_codeable_concept: null,
        observation_date: null,
        unit_code: null,
        unit: null,
        note: null,
      },
    ];
  };

  const submitRecord = async () => {
    const hasAtLeastOneSectionEntry = [
      chiefComplaint,
      knownCaseOf,
      historyOf,
      onObservation,
      upperLimbExam,
      lowerLimbExam,
      advice,
    ].some((sectionRows) => Array.isArray(sectionRows) && sectionRows.length > 0);

    if (!hasAtLeastOneSectionEntry) {
      toast.error("Please add at least one entry in any section before submission.");
      return;
    }

    if (!selectedContext) {
      toast.error("Please select a visit purpose.");
      return;
    }
    const currentDate = getCurrentDateTime();
    const payload = {
      HIP: facilityId,
      file_data: [
        {
          careContextID: selectedContext,
          patientID: patientId,
          date: currentDate,
          encounterStartDate: currentDate,
          encounterEndDate: currentDate,
          practitioner: doctorName,
          practitionerID: doctorId,
          detail: [
            {
              resource_type: "Encounter",
              encounter_id: selectedContext,
              start_date: currentDate,
              end_date: currentDate,
            },
            ...toConditionPayload("Condition", chiefComplaint),
            ...toConditionPayload("KnownCaseOf", knownCaseOf),
            ...toConditionPayload("HistoryOf", historyOf),
            ...toObservationPayload("PhysioObservation", onObservation),
            ...toObservationPayload("UpperLimbExam", upperLimbExam),
            ...toObservationPayload("LowerLimbExam", lowerLimbExam),
            ...buildAdviceAsOtherObservation(advice),
          ],
        },
      ],
      app_mode: appMode,
      org_code: orgCode,
    };

    try {
      await axios.post(`${backendUrl}op_consultrecord/`, payload, { headers });

      const selectedCareContext = careContexts.find(
        (cc) => cc.care_context_reference === selectedContext
      );
      const isFacilityAbdmEnabled =
        isAbdmEnabled === true || isAbdmEnabled === "true";

      if (
        isFacilityAbdmEnabled &&
        patientData?.abha_id &&
        selectedCareContext &&
        !selectedCareContext.abdm_linked
      ) {
        // Keep payload structure exactly like Doctor Console.
        const postBody = {
          add_context: {
            link: {
              patient: {
                careContexts: [
                  {
                    display: selectedContextDisplay,
                    referenceNumber: selectedContext,
                  },
                ],
                display: patientData?.abha_id,
                referenceNumber: patientId,
              },
            },
          },
          auth_confirm: {
            credential: {
              demographic: {
                authCode: "",
                dateOfBirth: patientData?.dob,
                gender: patientData?.gender?.substring(0, 1),
                name: patientData?.name,
              },
            },
          },
          auth_init: {
            query: {
              authMode: "DEMOGRAPHICS",
              id: patientData?.abha_id,
              purpose: "KYC_AND_LINK",
              requester: {
                id: facilityId,
                type: "HIP",
              },
            },
          },
        };

        await axios.post(
          `${backendUrl}auth_init_workflow_new/`,
          JSON.stringify(postBody),
          {
            headers: headers,
          }
        );

        // Same endpoint/payload/header shape as Doctor Console.
        const messagePostBody = {
          hip: {
            id: facilityId,
            name: facilityId,
          },
          phoneNo: patientData?.phone_no,
        };
        await axios.post(
          `${backendUrl}new_health_record_notification_for_existing_care_context_sms_endpoint/`,
          messagePostBody,
          {
            headers: headers,
          }
        );
      }

      setSubmitted(true);
      toast.success("Physiotherapy record submitted successfully.");
    } catch (error) {
      toast.error("Unable to submit physiotherapy record.");
    }
  };

  const handlePrint = async (withLetterhead) => {
    if (!printRef.current) return;
    await printRef.current.print(withLetterhead);
    setPrintAnchorEl(null);
  };

  const sectionData = {
    chiefComplaint,
    knownCaseOf,
    historyOf,
    onObservation,
    upperLimbExam,
    lowerLimbExam,
    advice,
  };

  const sharedConditionColumns = [
    {
      key: "disease_description",
      label: "Condition Name",
      tableLabel: "Condition",
      type: "cdac",
      semanticTag: "finding++disorder",
      codeKey: "disease_code",
      width: 3,
    },
    { key: "clinical_status_description", label: "Clinical Status", type: "select", options: clinicalStatusOptions, width: 3 },
  ];

  return (
    <Dialog open={open} onClose={handleModalClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ backgroundColor: "#213360", color: "#fff", pr: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Physiotherapy Sheet</Typography>
          <IconButton onClick={handleModalClose} sx={{ color: "#fff" }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#F4F7FA", py: 2 }}>
        <Card sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={5}>
              <TextField
                select
                fullWidth
                size="small"
                label="Visit Purpose"
                value={selectedContext}
                onChange={(e) => {
                  const selected = careContexts.find(
                    (item) => item.care_context_reference === e.target.value
                  );
                  setSelectedContext(e.target.value);
                  setSelectedContextDisplay(selected?.display || "");
                }}
              >
                {careContexts.map((item) => (
                  <MenuItem key={item.care_context_reference} value={item.care_context_reference}>
                    {item.description} {item.care_context_reference}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Add Visit Purpose"
                value={newVisitPurpose}
                onChange={(e) => setNewVisitPurpose(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <StyledButton variant="contained" bgColor="#0E3F66" sx={{ width: "100%", height: 40 }} onClick={addCareContext}>
                Add
              </StyledButton>
            </Grid>
          </Grid>
        </Card>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <PhysioEditableSection
            title="Chief Complaint"
            rows={chiefComplaint}
            setRows={setChiefComplaint}
            columns={sharedConditionColumns}
            onCdacSearch={searchCdac}
            requiredFields={["disease_description", "clinical_status_description"]}
          />
          <PhysioEditableSection
            title="Known Case Of"
            rows={knownCaseOf}
            setRows={setKnownCaseOf}
            columns={sharedConditionColumns}
            onCdacSearch={searchCdac}
            requiredFields={["disease_description", "clinical_status_description"]}
          />
          <PhysioEditableSection
            title="History Of"
            rows={historyOf}
            setRows={setHistoryOf}
            columns={[...sharedConditionColumns, { key: "onset_datetime", label: "Onset Date", type: "date", width: 2 }]}
            onCdacSearch={searchCdac}
            requiredFields={["disease_description", "clinical_status_description"]}
          />
          <PhysioEditableSection
            title="On Observation"
            rows={onObservation}
            setRows={setOnObservation}
            key={`on-observation-${selectedContext}`}
            columns={[
              {
                key: "observation_description",
                label: "Observation",
                type: "cdac",
                semanticTag: "observable entity",
                codeKey: "observation_code",
                width: 3,
              },
              { key: "value_string", label: "Examination Notes", width: 4, multiline: true },
              {
                key: "observation_date",
                label: "Observation Date",
                type: "date",
                width: 2,
                defaultValue: getSelectedCareContextDate(),
              },
            ]}
            onCdacSearch={searchCdac}
            requiredFields={["observation_description"]}
          />
          <PhysioEditableSection
            title="On Examination - Upper Limb"
            rows={upperLimbExam}
            setRows={setUpperLimbExam}
            key={`upper-limb-${selectedContext}`}
            columns={[
              {
                key: "observation_description",
                label: "Test Name",
                type: "cdac",
                semanticTag: "observable entity",
                codeKey: "observation_code",
                width: 3,
              },
              { key: "value_quantity", label: "Value/Quantity", type: "number", width: 2 },
              { key: "value_string", label: "Examination Notes", width: 4, multiline: true },
              { key: "unit", label: "Unit", width: 2 },
              {
                key: "body_site_description",
                label: "Body Site",
                type: "cdac",
                semanticTag: "body structure",
                codeKey: "body_site_code",
                width: 2,
              },
              {
                key: "observation_date",
                label: "Observation Date",
                type: "date",
                width: 2,
                defaultValue: getSelectedCareContextDate(),
              },
            ]}
            onCdacSearch={searchCdac}
            requiredFields={["observation_description"]}
          />
          <PhysioEditableSection
            title="On Examination - Lower Limb"
            rows={lowerLimbExam}
            setRows={setLowerLimbExam}
            key={`lower-limb-${selectedContext}`}
            columns={[
              {
                key: "observation_description",
                label: "Test Name",
                type: "cdac",
                semanticTag: "observable entity",
                codeKey: "observation_code",
                width: 3,
              },
              { key: "value_string", label: "Examination Notes", width: 3, multiline: true },
              {
                key: "body_site_description",
                label: "Body Site",
                type: "cdac",
                semanticTag: "body structure",
                codeKey: "body_site_code",
                width: 3,
              },
              {
                key: "observation_date",
                label: "Observation Date",
                type: "date",
                width: 2,
                defaultValue: getSelectedCareContextDate(),
              },
            ]}
            onCdacSearch={searchCdac}
            requiredFields={["observation_description"]}
          />
          <PhysioEditableSection
            title="Advice"
            rows={advice}
            setRows={setAdvice}
            columns={[{ key: "advice_text", label: "Advice", width: 8, multiline: true }]}
            onCdacSearch={searchCdac}
            requiredFields={["advice_text"]}
            maxEntries={1}
          />
        </Box>

      </DialogContent>
      <DialogActions
        sx={{
          flexShrink: 0,
          justifyContent: "center",
          px: 2,
          py: 1.5,
          minHeight: 56,
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Grid container spacing={1.5}>
            <Grid item xs={submitted ? 3 : 6}>
              <StyledButton
                variant="contained"
                bgColor="#125386"
                sx={{ width: "100%" }}
                onClick={submitRecord}
              >
                Submit
              </StyledButton>
            </Grid>
            <Grid item xs={submitted ? 3 : 6}>
              <StyledButton
                variant="contained"
                bgColor="#BAF0EB"
                textColor="#000"
                borderColor="#21CDC0"
                sx={{ width: "100%" }}
                onClick={handleModalClose}
              >
                Close
              </StyledButton>
            </Grid>
            {submitted ? (
              <>
                <Grid item xs={3}>
                  <StyledButton
                    variant="contained"
                    bgColor="#FFD2B0"
                    textColor="#FF6F00"
                    borderColor="#FF8C33"
                    sx={{ width: "100%" }}
                    onClick={(e) => setPrintAnchorEl(e.currentTarget)}
                  >
                    Print
                  </StyledButton>
                  <Menu
                    anchorEl={printAnchorEl}
                    open={Boolean(printAnchorEl)}
                    onClose={() => setPrintAnchorEl(null)}
                  >
                    <MenuItem onClick={() => handlePrint(true)}>
                      Physiotherapy Sheet with Letterhead
                    </MenuItem>
                    <MenuItem onClick={() => handlePrint(false)}>
                      Physiotherapy Sheet without Letterhead
                    </MenuItem>
                  </Menu>
                </Grid>
                <Grid item xs={3}>
                  <StyledButton
                    variant="contained"
                    bgColor="#8DC63F"
                    borderColor="#5DAA60"
                    sx={{ width: "100%" }}
                    onClick={() => {
                      resetFormState();
                      onCompleteAppointment?.(appointment?.appointment_id);
                    }}
                  >
                    Complete
                  </StyledButton>
                </Grid>
              </>
            ) : null}
          </Grid>
        </Box>
      </DialogActions>

      <PrintPhysiotherapy
        ref={printRef}
        sectionsData={sectionData}
        patientData={patientData}
        doctorData={selectedDoctorData}
        selectedContextDisplay={selectedContextDisplay}
        selectedContext={selectedContext}
        dateForAppointment={dateForAppointment}
        startTime={appointment?.start_time}
      />
    </Dialog>
  );
}
